import { TRANSLATIONS } from './translations';

export type CustomSpecFieldType = 'number' | 'boolean' | 'text' | 'select';

export interface CustomSpecFieldOption {
  value: string;
  labelKey: string;
}

export interface CustomSpecFieldDef {
  /** Key inside the CustomAttributesJson object -- stable, never renamed once a listing exists with it set. */
  key: string;
  labelKey: string;
  type: CustomSpecFieldType;
  /** Shown next to the label/value, e.g. "m", "m³", "kg", "L/min", "bar", "mm" -- omitted for boolean/text/select fields. */
  unitKey?: string;
  /** Only for type: 'select'. */
  options?: CustomSpecFieldOption[];
}

// The "Nivel B" per-type attributes from the Yémin/Gemini technical spec --
// stored as a single opaque JSON object per Machinery (see
// Machinery.CustomAttributesJson's own doc comment), rendered as real form
// inputs here by MachineryType.Code so the host fills in exactly the specs
// that matter for what they're actually listing, not a generic free-text
// box. Deliberately keyed by Code, not by MachineryFamily -- families like
// "Movimiento de Tierras" mix very different machines (an excavator's specs
// don't apply to a motor grader in the same family), so a family-level
// schema would either force irrelevant fields onto some types or miss
// relevant ones on others.
//
// Only the 4 groups the original proposal gave concrete fields for are
// covered (20 of the 83 types) -- every other type gets no extra section on
// the form/detail page this round, just the global spec fields everything
// already has (weight/power/reach/propulsion/fuel/transport dims). Adding
// another type's schema later is a pure data change here, no form/detail
// code to touch.
const EXCAVATOR_FIELDS: CustomSpecFieldDef[] = [
  { key: 'maxExcavationDepthM', labelKey: TRANSLATIONS.MACHINERY_SPEC_MAX_EXCAVATION_DEPTH_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M },
  { key: 'maxHorizontalReachM', labelKey: TRANSLATIONS.MACHINERY_SPEC_MAX_HORIZONTAL_REACH_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M },
  { key: 'bucketCapacityM3', labelKey: TRANSLATIONS.MACHINERY_SPEC_BUCKET_CAPACITY_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M3 },
  { key: 'hasAuxHydraulicLine', labelKey: TRANSLATIONS.MACHINERY_SPEC_HAS_AUX_HYDRAULIC_LINE_LABEL, type: 'boolean' },
];

const LIFTING_FIELDS: CustomSpecFieldDef[] = [
  { key: 'maxWorkingHeightM', labelKey: TRANSLATIONS.MACHINERY_SPEC_MAX_WORKING_HEIGHT_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M },
  { key: 'maxLoadCapacityKg', labelKey: TRANSLATIONS.MACHINERY_SPEC_MAX_LOAD_CAPACITY_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_KG },
  { key: 'outreachM', labelKey: TRANSLATIONS.MACHINERY_SPEC_OUTREACH_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M },
];

const HYDRAULIC_ATTACHMENT_FIELDS: CustomSpecFieldDef[] = [
  { key: 'attachmentWeightKg', labelKey: TRANSLATIONS.MACHINERY_SPEC_ATTACHMENT_WEIGHT_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_KG },
  { key: 'hydraulicFlowLMin', labelKey: TRANSLATIONS.MACHINERY_SPEC_HYDRAULIC_FLOW_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_L_MIN },
  { key: 'workingPressureBar', labelKey: TRANSLATIONS.MACHINERY_SPEC_WORKING_PRESSURE_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_BAR },
  { key: 'tipDiameterMm', labelKey: TRANSLATIONS.MACHINERY_SPEC_TIP_DIAMETER_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_MM },
  { key: 'compatibleCarrierWeightRange', labelKey: TRANSLATIONS.MACHINERY_SPEC_COMPATIBLE_CARRIER_WEIGHT_LABEL, type: 'text' },
];

const DOZER_GRADER_FIELDS: CustomSpecFieldDef[] = [
  { key: 'bladeWidthM', labelKey: TRANSLATIONS.MACHINERY_SPEC_BLADE_WIDTH_LABEL, type: 'number', unitKey: TRANSLATIONS.MACHINERY_SPEC_UNIT_M },
  {
    key: 'bladeType',
    labelKey: TRANSLATIONS.MACHINERY_SPEC_BLADE_TYPE_LABEL,
    type: 'select',
    options: [
      { value: 'PAT', labelKey: TRANSLATIONS.MACHINERY_SPEC_BLADE_TYPE_PAT },
      { value: 'SemiU', labelKey: TRANSLATIONS.MACHINERY_SPEC_BLADE_TYPE_SEMI_U },
      { value: 'Straight', labelKey: TRANSLATIONS.MACHINERY_SPEC_BLADE_TYPE_STRAIGHT },
    ],
  },
  { key: 'hasRipper', labelKey: TRANSLATIONS.MACHINERY_SPEC_HAS_RIPPER_LABEL, type: 'boolean' },
  { key: 'has3dGpsGuidance', labelKey: TRANSLATIONS.MACHINERY_SPEC_HAS_3D_GPS_LABEL, type: 'boolean' },
];

export const MACHINERY_CUSTOM_SPEC_FIELDS_BY_TYPE: Record<string, CustomSpecFieldDef[]> = {
  // Excavadoras / Retroexcavadoras
  'excavadora-de-cadenas': EXCAVATOR_FIELDS,
  'excavadora-de-ruedas': EXCAVATOR_FIELDS,
  'miniexcavadora-midiexcavadora': EXCAVATOR_FIELDS,
  'retroexcavadora-mixta': EXCAVATOR_FIELDS,

  // Maquinaria de Elevación (Plataformas, Manipuladores, Grúas)
  'manipulador-telescopico-fijo': LIFTING_FIELDS,
  'manipulador-telescopico-giratorio': LIFTING_FIELDS,
  'grua-movil-telescopica': LIFTING_FIELDS,
  'grua-sobre-orugas': LIFTING_FIELDS,
  'grua-torre-automontable': LIFTING_FIELDS,
  'plataforma-elevadora-articulada-telescopica-tijera': LIFTING_FIELDS,

  // Martillos Hidráulicos / Implementos (todo Sector 8 -- Familia 15)
  'martillo-hidraulico': HYDRAULIC_ATTACHMENT_FIELDS,
  'cizalla-pinza-de-demolicion-o-chatarra': HYDRAULIC_ATTACHMENT_FIELDS,
  'cazo-de-excavacion-limpieza-criba': HYDRAULIC_ATTACHMENT_FIELDS,
  'enganche-basculante-tiltrotator': HYDRAULIC_ATTACHMENT_FIELDS,
  'fresadora-de-asfalto-hormigon': HYDRAULIC_ATTACHMENT_FIELDS,
  'ahoyador-barrena': HYDRAULIC_ATTACHMENT_FIELDS,
  'pulpo-para-chatarra-madera': HYDRAULIC_ATTACHMENT_FIELDS,
  'cesta-homologada-plumin-para-manipulador': HYDRAULIC_ATTACHMENT_FIELDS,

  // Bulldozers y Niveladoras
  'bulldozer-tractor-de-orugas': DOZER_GRADER_FIELDS,
  motoniveladora: DOZER_GRADER_FIELDS,
};

export const getCustomSpecFields = (categoryCode?: string): CustomSpecFieldDef[] =>
  (categoryCode && MACHINERY_CUSTOM_SPEC_FIELDS_BY_TYPE[categoryCode]) || [];
